"use client";

import { useTranslation } from "react-i18next";
import { CheckCircle, Globe, MapPin, Pencil } from "lucide-react";
import { motion } from "motion/react";

import { Button } from "@/components/ui/button";
import { useProfileEditDialogStore } from "@/features/profile/tutor/store/useProfileEditDialogStore";
import { Card } from "@/components/ui/card";
import { useProfileDataStore } from "@/features/profile/tutor/store/useProfileDataStore";
import { CoachMarker } from "@/components/CoachMarker.tsx";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip.tsx";

export function HeaderDisplay() {
  const { profileData } = useProfileDataStore();
  const { t } = useTranslation(["help", "common"]);
  const data = profileData?.header_section;
  const { open } = useProfileEditDialogStore();

  if (!data) return null;

  return (
    <CoachMarker
      description={t("help:coachMarkers.profileOverview.description")}
      id="profile-overview"
      placement="right"
      title={t("help:coachMarkers.profileOverview.title")}
    >
      <Card className="bg-side-accent/50 relative overflow-hidden rounded-2xl border-none shadow-2xl backdrop-blur-xl">
        <motion.div>
          <div className="bg-muted/20 relative h-[200px] w-full rounded-t-2xl">
            {data.background_image && (
              <img
                alt=""
                className="h-full w-full rounded-t-2xl object-cover"
                src={data.background_image || "/placeholder.svg"}
              />
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="bg-foreground/50 absolute top-[210px] right-4 z-10 rounded-xl backdrop-blur-sm"
                    size="sm"
                    variant="ghostSecondary"
                    onClick={() => open("header")}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  <p>{t("common:edit")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="relative px-6 pb-6">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute -right-20 -bottom-20 h-40 w-40 rounded-full bg-gradient-to-br from-blue-400 to-purple-600" />
              <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-green-400 to-blue-500" />
            </div>

            <motion.div
              animate={{ y: 0, opacity: 1 }}
              initial={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="relative -mt-16 mb-4 flex items-end justify-between">
                <div className="flex items-end gap-4">
                  <div className="border-primary bg-background/80 relative h-32 w-32 overflow-hidden rounded-2xl border-2 shadow-xl backdrop-blur-sm">
                    <img
                      alt={data.name}
                      className="h-full w-full object-cover"
                      src={data.avatar_image || "/placeholder.svg"}
                    />
                  </div>
                </div>
              </div>
              <div className="relative space-y-1">
                <motion.div
                  animate={{ y: 0, opacity: 1 }}
                  initial={{ y: 10, opacity: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <div className="flex items-center gap-2">
                    <h1 className="text-accent-foreground text-2xl font-bold">{data.name}</h1>
                    {data.verification_status === "VERIFIED" && (
                      <CheckCircle className="text-primary h-5 w-5" />
                    )}
                  </div>
                </motion.div>
                <motion.p
                  animate={{ y: 0, opacity: 1 }}
                  className="text-muted-foreground text-base"
                  initial={{ y: 10, opacity: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  {data.headline}
                </motion.p>
                {data.current_location_name && (
                  <motion.div
                    animate={{ y: 0, opacity: 1 }}
                    className="text-muted-foreground flex items-center text-sm"
                    initial={{ y: 10, opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    {data.current_location_name.includes(",") ? (
                      <MapPin className="mr-1 h-4 w-4" />
                    ) : (
                      <Globe className="mr-1 h-4 w-4" />
                    )}
                    {data.current_location_name}
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </Card>
    </CoachMarker>
  );
}
